import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, ScanLine, Keyboard as KeyboardIcon, CameraOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";

// BarcodeScanner — open a camera modal, scan an EAN/UPC/QR using the native
// BarcodeDetector API (Chrome/Edge on Android, Safari iOS 17+), fall back to
// a manual entry input on unsupported browsers. When the scanned code is a
// QR code we try to parse it as a JSON tag payload (from our own Tag
// Generator) first; anything else routes to the Open Food Facts EAN/UPC
// lookup. Callback: onScanned({ code, name, brand, unit_hint, image_url,
// ..., is_tag?, tag? }).
export default function BarcodeScanner({ open, onClose, onScanned, mode = "any" }) {
  // mode: "any" (default — barcodes + QR) | "qr" (QR-only) | "barcode" (EAN/UPC only)
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cancelledRef = useRef(false);
  const [supported, setSupported] = useState(true);
  const [manual, setManual] = useState("");
  const [error, setError] = useState("");
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [scannedCode, setScannedCode] = useState("");

  const stopCamera = () => {
    cancelledRef.current = true;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    cancelledRef.current = false;
    setError("");
    setScannedCode("");
    const hasApi = typeof window !== "undefined" && "BarcodeDetector" in window;
    setSupported(hasApi);

    if (!hasApi) return;

    (async () => {
      let detector;
      // Format list depends on the caller-requested mode. "any" gets both.
      const formatsByMode = {
        any:     ["qr_code", "ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39"],
        qr:      ["qr_code"],
        barcode: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39"],
      };
      try {
        // eslint-disable-next-line no-undef
        detector = new BarcodeDetector({ formats: formatsByMode[mode] || formatsByMode.any });
      } catch (e) {
        setSupported(false);
        setError("Camera scanner isn't supported on this browser. Type the code below instead.");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (e) {
        setError("Camera permission denied. Type the barcode below instead.");
        return;
      }

      const scanLoop = async () => {
        if (cancelledRef.current || !videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes.length && !cancelledRef.current) {
            const raw = codes[0].rawValue;
            const fmt = codes[0].format || "";
            setScannedCode(raw);
            stopCamera();
            await handleScanned(raw, fmt);
            return;
          }
        } catch (_) { /* keep trying */ }
        setTimeout(scanLoop, 250);
      };
      scanLoop();
    })();

    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A find2near tag QR is either a JSON object with "kind": "f2n_tag" or a URL
  // like `find2near://tag?data=<base64>`. We accept both. Any other QR content
  // is returned as `{ code, is_url }` so the caller can decide what to do.
  const tryParseTag = (raw) => {
    if (!raw) return null;
    // Direct JSON payload
    try {
      const j = JSON.parse(raw);
      if (j && (j.kind === "f2n_tag" || j.item_code || j.gross_weight != null)) {
        return { ...j, kind: "f2n_tag" };
      }
    } catch { /* not JSON */ }
    // find2near://tag?data=<base64>
    const m = raw.match(/^find2near:\/\/tag\?data=([A-Za-z0-9+/=_-]+)/i);
    if (m) {
      try {
        const decoded = atob(m[1].replace(/-/g, "+").replace(/_/g, "/"));
        const j = JSON.parse(decoded);
        return { ...j, kind: "f2n_tag" };
      } catch { /* corrupted payload */ }
    }
    return null;
  };

  const handleScanned = async (raw, fmt) => {
    // QR codes get tag-parsing first; non-QR go straight to EAN lookup.
    const isQr = fmt === "qr_code" || /^\s*[\{[]/.test(raw) || raw.startsWith("find2near://") || raw.startsWith("http");
    if (isQr) {
      const tag = tryParseTag(raw);
      if (tag) {
        toast.success(`Tag scanned: ${tag.item_code || "item"}`);
        onScanned?.({ code: raw, is_tag: true, tag });
        onClose?.();
        return;
      }
      // Plain QR (URL, WIFI, etc.) — return raw content.
      toast.success("QR scanned");
      onScanned?.({ code: raw, is_qr: true, is_url: /^https?:\/\//i.test(raw) });
      onClose?.();
      return;
    }
    // EAN/UPC → Open Food Facts lookup.
    await doLookup(raw);
  };

  const doLookup = async (code) => {
    setLoadingLookup(true);
    try {
      const r = await api.post("/erp/barcode-lookup", { code });
      toast.success(`Found: ${r.data.name}`);
      onScanned?.(r.data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Lookup failed";
      setError(msg + " — you can still type the details manually.");
    } finally {
      setLoadingLookup(false);
    }
  };

  const submitManual = (e) => {
    e.preventDefault();
    const code = manual.trim();
    if (code.length < 6) return toast.error("Enter at least 6 digits");
    doLookup(code);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="barcode-scanner-modal">
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <ScanLine className="text-emerald-600" size={18} />
            <div className="font-bold text-stone-900">Scan barcode</div>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="barcode-scanner-close">
            <X size={20} />
          </button>
        </div>

        {supported && !error && (
          <div className="relative aspect-[4/3] bg-stone-950">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted data-testid="barcode-scanner-video" />
            {/* target overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-3/4 h-24 border-2 border-emerald-400 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
            </div>
            <div className="absolute bottom-3 left-0 right-0 text-center text-white/80 text-xs">
              Point the camera at an EAN/UPC barcode
            </div>
            {loadingLookup && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white gap-2">
                <Loader2 className="animate-spin" size={18} />
                <span className="text-sm">Looking up {scannedCode}…</span>
              </div>
            )}
          </div>
        )}

        {(!supported || error) && (
          <div className="p-6 text-center bg-stone-50 border-b border-stone-200">
            <CameraOff className="mx-auto text-stone-400 mb-2" size={28} />
            <div className="text-sm text-stone-700 leading-relaxed">
              {error || "Camera scanning isn't available on this browser."}
            </div>
          </div>
        )}

        <form onSubmit={submitManual} className="p-5 border-t border-stone-100 space-y-2">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 flex items-center gap-1.5">
            <KeyboardIcon size={12} /> Or type the barcode
          </div>
          <div className="flex gap-2">
            <Input
              value={manual}
              onChange={e => setManual(e.target.value.replace(/[^0-9]/g, ""))}
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="e.g. 3017620422003"
              className="rounded-xl font-mono"
              data-testid="barcode-scanner-manual"
              autoFocus={!supported}
            />
            <Button type="submit" disabled={loadingLookup} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="barcode-scanner-lookup">
              {loadingLookup ? <Loader2 className="animate-spin" size={14} /> : "Look up"}
            </Button>
          </div>
          <p className="text-[10px] text-stone-500">Powered by Open Food Facts — free global product database.</p>
        </form>
      </div>
    </div>
  );
}
